import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import "./../styles/theme.css";
import { fetchLatestWebfuseArticle } from "../utils/webfuseWebhookFetch";
import { fetchSentimentFromLatest } from "../utils/sentimentApi";
import useCache from "../hooks/useCache";
import propaganda_quotes from "../data/quotes.json";

// ---------- logging throttles (module scope, no functional changes) ----------
const SP_LOG_EVERY_MS = 60000;
let spLastHashPrinted = null;
let spLastHashLogTs = 0;
let spLastNoopLogTs = 0;
let spLastStartSameLogTs = 0;
const spLoggedCacheHit = new Set(); // hashes we've already logged "cache HIT" for

// Hash helper
function hashArticle({ articleTitle = "", articlePublication = "", articleContent = "" }) {
  const str = `${articleTitle}\n${articlePublication}\n${articleContent}`;
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h) ^ str.charCodeAt(i);
  return (h >>> 0).toString(36);
}

/** Collapsible Section (unchanged) */
function CollapsibleSection({ headerLeft, defaultOpen = false, children, ariaLabel }) {
  const [open, setOpen] = useState(defaultOpen);
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [measured, setMeasured] = useState(0);

  useEffect(() => {
    if (!innerRef.current) return;
    const ro = new ResizeObserver(() => setMeasured(innerRef.current?.scrollHeight || 0));
    ro.observe(innerRef.current);
    setMeasured(innerRef.current?.scrollHeight || 0);
    return () => ro.disconnect();
  }, []);

  const icon = open ? "/img/expand-v.svg" : "/img/collapse-v.svg";

  return (
    <div className="section">
      <div className="section-head">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 8 }}>
          <span>{headerLeft}</span>
          <button
            className="bubble-btn"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={ariaLabel || (open ? "Collapse section" : "Expand section")}
            style={{ padding: 4, display: "inline-flex", alignItems: "center" }}
          >
            <img src={icon} alt="" className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div
        ref={outerRef}
        style={{
          overflow: "hidden",
          maxHeight: open ? measured : 0,
          opacity: open ? 1 : 0,
          transform: open ? "translateY(0)" : "translateY(-4px)",
          transition: "max-height 240ms ease, opacity 180ms ease, transform 240ms ease",
          pointerEvents: open ? "auto" : "none",
        }}
      >
        <div ref={innerRef} style={{ paddingTop: 8 }}>{children}</div>
      </div>
    </div>
  );
}

export default function SentimentPanel() {
  // Phases: "start" | "waiting" | "rendering" | "ready"
  const [phase, setPhase] = useState("start");
  const [parsed, setParsed] = useState(null);
  const [altIdx, setAltIdx] = useState(0);


  const infoRef = useRef(null);
  const [tt, setTt] = useState({ visible: false, top: 0, left: 0 });

  // Control the small "alternatives" block under HEADLINE
  const [altsOpen, setAltsOpen] = useState(false);

  // Reusable collapse
function Collapse({ open, children }) {
  const outerRef = React.useRef(null);
  const innerRef = React.useRef(null);

  // Set initial visual state on mount
  React.useLayoutEffect(() => {
    const outer = outerRef.current;
    if (!outer) return;
    outer.style.overflow = "hidden";
    outer.style.opacity = open ? "1" : "0";
    outer.style.maxHeight = open ? "none" : "0px";
    outer.style.pointerEvents = open ? "auto" : "none";
    outer.style.transition = "none"; // no flash on mount
  }, []); // mount only

  // Open/close transitions (no transform)
  React.useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;

    const DUR = 240;

    if (open) {
      // Opening: 0 -> measured px -> settle to auto
      const target = inner.scrollHeight + "px";
      outer.style.transition = "max-height 240ms ease, opacity 180ms ease";
      outer.style.pointerEvents = "auto";
      outer.style.opacity = "1";

      if (outer.style.maxHeight === "none") {
        // coming from a previous open — snap to px then animate
        outer.style.transition = "none";
        outer.style.maxHeight = inner.scrollHeight + "px";
        // force reflow
        // eslint-disable-next-line no-unused-expressions
        outer.getBoundingClientRect();
        outer.style.transition = "max-height 240ms ease, opacity 180ms ease";
      }
      requestAnimationFrame(() => {
        outer.style.maxHeight = target;
      });

      const settleId = setTimeout(() => {
        // remove max-height animation while open so internal changes don't re-animate
        outer.style.transition = "opacity 180ms ease";
        outer.style.maxHeight = "none";
      }, DUR);
      return () => clearTimeout(settleId);
    } else {
      // Closing: auto|px -> px -> 0
      const current = inner.scrollHeight + "px";
      outer.style.transition = "none";
      outer.style.maxHeight = current;
      // force reflow
      // eslint-disable-next-line no-unused-expressions
      outer.getBoundingClientRect();
      outer.style.transition = "max-height 240ms ease, opacity 180ms ease";
      requestAnimationFrame(() => {
        outer.style.maxHeight = "0px";
        outer.style.opacity = "0";
        outer.style.pointerEvents = "none";
      });
    }
  }, [open]);

  return (
    <div ref={outerRef} className={`collapse-outer ${open ? "is-open" : ""}`}>
      <div ref={innerRef} className="collapse-inner">
        {children}
      </div>
    </div>
  );
}


  // Cache (persisted in localStorage via your hook)
  const {
    sentimentCache,
    setSentimentCache,
    lastSentimentHash,
    setLastSentimentHash,
    resetCounter,       // optional: incremented by useCache on clear
    cacheClearedAt,     // optional: timestamp set by useCache on clear
  } = useCache();

  // Track which hash is currently DISPLAYED to avoid re-flipping phase
  const displayedHashRef = useRef(lastSentimentHash || null);

  // Ignore the first "old" hash seen while in start until a NEW hash appears
  const startSeenHashRef = useRef(null);

  // In-flight control
  const inFlightRef = useRef(false);

  // Epoch guards to cancel stale async after a reset
  const epochRef = useRef(0);
  const inFlightEpochRef = useRef(0);
  const acceptFirstHashAfterResetRef = useRef(false);
  const inFlightHashRef = useRef(null);
  const resetSinceRef = useRef(0);
  const hasNewPostSinceReset = () => {
    try { return !!localStorage.getItem("newsCompare:lastPostedArticle"); }
    catch { return false; }
  };
  // Per-hash retry control
  const requestIdRef = useRef(0);          // at most 2 total attempts per hash (first + one retry)
  const [waitNote, setWaitNote] = useState(null);
  const waitNoteVerRef = useRef(0);

  useEffect(() => {
    function onArticleSubmitted(e) {
      window.dispatchEvent(new CustomEvent('newscompare:busy', { detail: { busy: true, reason: 'article_submit' } }));
      waitNoteVerRef.current++; // invalidate any old timers
      // Immediately hide old content and show the waiting overlay
      setParsed(null);
      setPhase('waiting');
      setWaitNote('Submitting URL to Webfuse');

      // Optional: clear “start” guards so the next hash is accepted
      startSeenHashRef.current = null;
      acceptFirstHashAfterResetRef.current = true;
    }
    window.addEventListener('newscompare:article-submitted', onArticleSubmitted);
    return () => window.removeEventListener('newscompare:article-submitted', onArticleSubmitted);
  }, []);

  function shuffle(a) {
    const c = a.slice();
    for (let i = c.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [c[i], c[j]] = [c[j], c[i]]; // fixed
    }
    return c;
  }

  const EyeGif = React.memo(() => <img src="/img/eye-motion.gif?v=1" alt="Loading…" className="wait-gif" decoding="async" draggable={false} />);
  function QuoteRotator({ order, intervalMs = 6000 }) {
    const [idx, setIdx] = React.useState(0);
    const [fading, setFading] = React.useState(false);
    React.useEffect(() => {
      const id = setInterval(() => {
        setFading(true);
        const t = setTimeout(() => { setIdx((i) => (i + 1) % order.length); setFading(false); }, 220);
        return () => clearTimeout(t);
      }, intervalMs);
      return () => clearInterval(id);
    }, [order.length, intervalMs]);
    const q = order[idx];
    return (
      <div className={`wait-quote ${fading ? "fade" : ""}`}>
        <div className="wait-quote-line"><span className="wait-quote-marks">“</span><span className="wait-quote-text">{q.text}</span><span className="wait-quote-marks">”</span></div>
        <div className="wait-quote-author">— {q.author}</div>
      </div>
    );
  }
  function Waiting({ note }) {
    const [order] = React.useState(() => shuffle(propaganda_quotes));
    const dots = useDots(450); // . .. ... .

    return (
      <div className="bias-panel wait-wrap">
        {/* bottom-right status; pulse + live for screen readers */}
        {note && (
          <div className="wait-status pulse" role="status" aria-live="polite">
            {note}{dots}
          </div>
        )}
        <EyeGif />
        <QuoteRotator order={order} intervalMs={5000} />
      </div>
    );
  }

  //helper for the wait notes
  function useDots(intervalMs = 450) {
    const [dots, setDots] = React.useState("");
    React.useEffect(() => {
      const steps = ["", ".", "..", "..."];
      let i = 0;
      const id = setInterval(() => {
        i = (i + 1) % steps.length;
        setDots(steps[i]);
      }, intervalMs);
      return () => clearInterval(id);
    }, [intervalMs]);
    return dots;
  }

  function showTt() {
    const el = infoRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const left = Math.min(Math.max(8, r.left - 140), window.innerWidth - 300);
    const top = Math.min(r.bottom + 8, window.innerHeight - 80);
    setTt({ visible: true, top, left });
  }
  function hideTt() { setTt((t) => ({ ...t, visible: false })); }

  /** Color helpers (unchanged) */
  const scaleColor = (v) => { const k = String(v || "").toLowerCase(); if (k === "very low") return "#b71c1c"; if (k === "low") return "#b71c1c"; if (k === "medium") return "#f4c430"; if (k === "high") return "#7bd48a"; if (k === "very high") return "#29c36a"; return "#f4c430"; };
  const hmlColor = (v) => { const k = String(v || "").toLowerCase(); if (k === "high") return "#7bd48a"; if (k === "medium") return "#f4c430"; if (k === "low") return "#b71c1c"; return "#f4c430"; };
  const biasColor = (v) => { const k = String(v || "").toLowerCase(); if (k === "high") return "#b71c1c"; if (k === "medium") return "#f4c430"; if (k === "low") return "#7bd48a"; return "#f4c430"; };

  // Helper: detect "empty" state of cache (no last hash AND no entries)
  const isEmptyCache = !lastSentimentHash && !Object.keys(sentimentCache || {}).length;

  // --- INITIAL HYDRATION ---
  useLayoutEffect(() => {
    const cache = sentimentCache || {};
    let h = lastSentimentHash;

    if (!h) {
      const keys = Object.keys(cache);
      if (keys.length) h = keys[keys.length - 1];
    }

    if (h && cache[h]) {
      setParsed(cache[h]);
      setPhase("ready");
      displayedHashRef.current = h;
      if (!lastSentimentHash) setLastSentimentHash(h);
      // Ensure we don't mistakenly accept a webhook hash on plain reload
      acceptFirstHashAfterResetRef.current = false;
      startSeenHashRef.current = null;
    } else {
      // Nothing cached on first load → stay in "start"
      setParsed(null);
      setPhase("start");
      displayedHashRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- LISTEN FOR EXTERNAL RESET SIGNALS ---
  useEffect(() => {
    if (typeof resetCounter !== "undefined" || typeof cacheClearedAt !== "undefined") {
      // noop — phase handled by isEmptyCache watcher
    }
  }, [resetCounter, cacheClearedAt]);

  useEffect(() => {
    function onCleared() {
      epochRef.current += 1;
      inFlightRef.current = false;
      inFlightEpochRef.current = epochRef.current;
      displayedHashRef.current = null;
      startSeenHashRef.current = null;
      inFlightHashRef.current = null;
      acceptFirstHashAfterResetRef.current = true;
      resetSinceRef.current = Date.now();
      setParsed(null);
      setPhase("start");
    }
    window.addEventListener("newscompare:cache-cleared", onCleared);
    return () => window.removeEventListener("newscompare:cache-cleared", onCleared);
  }, []);

  // --- FORCE START WHEN CACHE IS TRULY EMPTY (e.g., after clicking Refresh) ---
  useEffect(() => {
    if (isEmptyCache) {
      epochRef.current += 1;
      inFlightRef.current = false;
      inFlightEpochRef.current = epochRef.current;
      displayedHashRef.current = null;
      startSeenHashRef.current = null;
      inFlightHashRef.current = null;
      setParsed(null);
      setPhase("start");
    }
  }, [isEmptyCache]);

  // --- POLLING LOOP (kept running even in "start", but guarded) ---
  useEffect(() => {
    let cancelled = false;
    let timer;

    async function tick() {
      const localEpoch = epochRef.current; // capture epoch at tick start

      try {
        const latest = await fetchLatestWebfuseArticle();
        if (cancelled || localEpoch !== epochRef.current) return; // aborted by reset

        const hasArticle = latest?.articleTitle && latest?.articleContent;
        if (!hasArticle) return;

        const h = hashArticle(latest);

        // 1️⃣7️⃣ — computed hash (throttled unless hash changed)
        {
          const now = performance.now();
          if (h !== spLastHashPrinted || (now - spLastHashLogTs > SP_LOG_EVERY_MS)) {
            console.log('FLOW LOG: 1️⃣7️⃣ Panel: computed hash', { hash: h, t: now.toFixed(1) });
            spLastHashPrinted = h;
            spLastHashLogTs = now;
          }
        }

        // While in "start", ignore the first seen hash until *new* (unless manual refresh)
        // While in "start", ignore the first seen hash until *new* (unless manual refresh + new post)
        if (phase === "start") {
          if (acceptFirstHashAfterResetRef.current) {
            // Only accept the first hash after reset if the app has posted a NEW URL
            if (!hasNewPostSinceReset()) {
              // No new post yet → ignore webhook echoes of the previous article
              if (!startSeenHashRef.current) startSeenHashRef.current = h;
              // (optional sparse log) console.log('FLOW LOG: 🟨 start: waiting for new post after manual reset');
              return;
            }
            console.log('FLOW LOG: 🟢 Panel(start): accepting first hash after manual refresh (new article detected)', {
              hash: h, t: performance.now().toFixed(1)
            });
            acceptFirstHashAfterResetRef.current = false; // one-shot
            setWaitNote('Article content received from Webfuse');
            // fall through to HIT/MISS handling below
          } else {
            if (!startSeenHashRef.current) {
              console.log('FLOW LOG: 🟡 Panel(start): first hash seen; treating as prior content', {
                hash: h, t: performance.now().toFixed(1)
              });
              startSeenHashRef.current = h;
              return; // stay idle
            }
            if (h === startSeenHashRef.current) {
              const now = performance.now();
              if (now - spLastStartSameLogTs > SP_LOG_EVERY_MS) {
                console.log('FLOW LOG: 🟡 Panel(start): same as prior hash; remain idle', { hash: h, t: now.toFixed(1) });
                spLastStartSameLogTs = now;
              }
              return;
            }
            console.log('FLOW LOG: 🟢 Panel(start): NEW hash after start; proceeding', {
              oldHash: startSeenHashRef.current, newHash: h, t: performance.now().toFixed(1)
            });
            setWaitNote('Article content received from Webfuse');
          }
        }


        // If we're already showing this exact hash, do nothing.
        if (h === displayedHashRef.current) {
          const now = performance.now();
          if (now - spLastNoopLogTs > SP_LOG_EVERY_MS) {
            console.log('FLOW LOG: 1️⃣8️⃣ Panel: same hash — noop', {
              hash: h, t: now.toFixed(1)
            });
            spLastNoopLogTs = now;
          }
          return;
        }

        // If cached: hydrate without calling OpenAI
        if (sentimentCache[h]) {
          if (!spLoggedCacheHit.has(h)) {
            console.log('FLOW LOG: 1️⃣8️⃣ Panel: cache HIT', {
              hash: h, t: performance.now().toFixed(1)
            });
            spLoggedCacheHit.add(h);
          }
          if (!cancelled && localEpoch === epochRef.current) {
            setParsed(sentimentCache[h]);
            setLastSentimentHash(h);
            displayedHashRef.current = h;
            setPhase(parsed ? "rendering" : "ready");
          }
        } else if (!inFlightRef.current) {
          // If we’re already fetching for this hash, bail (dedupe)
          if (inFlightHashRef.current === h) {
            return;
          }
          console.log('FLOW LOG: 1️⃣8️⃣ Panel: cache MISS → call OpenAI', {
            hash: h, t: performance.now().toFixed(1)
          });
          const v = ++waitNoteVerRef.current;          // mark this step’s version
          setWaitNote('Sending article content to OpenAI');

          setTimeout(() => {
            if (waitNoteVerRef.current === v) {
              setWaitNote('AI is thinking - awaiting response');
            }
          }, 4000);

          window.dispatchEvent(new CustomEvent('newscompare:busy', { detail: { busy: true, reason: 'openai' } }));

          // Show loader and mark in-flight for this hash
          if (!cancelled && localEpoch === epochRef.current) setPhase("waiting");
          inFlightRef.current = true;
          inFlightHashRef.current = h;
          inFlightEpochRef.current = localEpoch;
          const reqId = ++requestIdRef.current;

          try {
            const result = await fetchSentimentFromLatest(); // { analysis }
            if (cancelled || localEpoch !== epochRef.current) return;
            if (reqId !== requestIdRef.current) return;

            const analysis = result?.analysis || null;
            if (analysis) {
              setSentimentCache((prev) => ({ ...prev, [h]: analysis }));
              setLastSentimentHash(h);
              displayedHashRef.current = h;
              setParsed(analysis);
              waitNoteVerRef.current++;
              setWaitNote('OpenAI responded — rendering');
              setPhase("rendering");
              console.log('FLOW LOG: 2️⃣1️⃣ Panel: analysis cached & parsed set', {
                hash: h, t: performance.now().toFixed(1)
              });
            }
          } finally {
            waitNoteVerRef.current++;
            if (localEpoch === epochRef.current) inFlightRef.current = false;
            if (inFlightHashRef.current === h) inFlightHashRef.current = null;
          }
        }


      } catch (e) {
        // Optional: quiet error logs here
        // console.error("SentimentPanel polling error:", e);
      } finally {
        if (!cancelled) timer = setTimeout(tick, 1200);
      }
    }

    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [phase, sentimentCache, lastSentimentHash, parsed]);

  // After we setParsed, flip to "ready" next paint
  useEffect(() => {
    if (!parsed) return;
    let raf1 = 0, raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        console.log('FLOW LOG: 2️⃣2️⃣ Panel: phase=ready', {
          shownHash: displayedHashRef.current,
          t: performance.now().toFixed(1)
        });
        setPhase("ready");
        setWaitNote(null);
        window.dispatchEvent(new CustomEvent('newscompare:busy', { detail: { busy: false } }));
      });
    });
    return () => { if (raf1) cancelAnimationFrame(raf1); if (raf2) cancelAnimationFrame(raf2); };
  }, [parsed]);

  // ----- Derived bits from parsed -----
  const rawAlts = parsed?.title_analysis?.alternative_titles || [];
  const altTitles = rawAlts.map((t) => (typeof t === "string" ? { title: t, category: "—" } : t));
  const activeAlt = altTitles[altIdx];
  const subjectTones = (parsed?.sentiment_tone?.towards_subjects || []).filter(
    (s) => s && String(s.subject || "").toLowerCase() !== "none" && String(s.tone || "").toLowerCase() !== "none"
  );

  // ---- RENDER ----

  // "start" phase UI (completely idle)
  if (phase === "start") {
    return (
      <div className="bias-panel start-empty">
        <div className="start-inner">
          <img src="/img/logo.svg" alt="" aria-hidden="true" className="start-logo" />
        </div>
      </div>
    );
  }

  // If we've never hydrated anything yet (but not in "start"), show the loader
  if (!parsed) return <Waiting note={waitNote} />;

  // While we transition from "rendering" to "ready", keep content in DOM but visually hidden
  const hiddenStyle = phase === "ready" ? undefined : { visibility: "hidden" };

  return (
    <>
      {/* Show overlay only when actually needed */}
      {phase !== "ready" && <Waiting note={waitNote} />}

      <div className="bias-panel" style={hiddenStyle}>
        {/* QUICK LOOK */}
        <CollapsibleSection headerLeft={<span>AT A GLANCE</span>} defaultOpen={false} ariaLabel="Toggle Quick Look">
          <div className="tiles3">
            <div className="tile-metric">
              <div className="tile-title">Bias</div>
              <div className="metric-bubble" style={{ background: biasColor(parsed?.accuracy_vs_bias?.bias) }}>
                {parsed?.accuracy_vs_bias?.bias || "Medium"}
              </div>
            </div>
            <div className="tile-metric">
              <div className="tile-title">Factual Accuracy</div>
              <div className="metric-bubble" style={{ background: hmlColor(parsed?.accuracy_vs_bias?.accuracy) }}>
                {parsed?.accuracy_vs_bias?.accuracy || "Medium"}
              </div>
            </div>
            <div className="tile-metric">
              <div className="tile-title">AI Analysis Quality</div>
              <div className="metric-bubble" style={{ background: scaleColor(parsed?.ai_confidence_score || "Medium") }}>
                {parsed?.ai_confidence_score || "Medium"}
              </div>
            </div>
          </div>
          <div className="spacer" />
          <div className="sentiment-desc">{parsed?.ai_confidence_rationale || ""}</div>
        </CollapsibleSection>

        {/* TONE CHECK (stacked) */}
        <CollapsibleSection
          headerLeft={
            <>
              <span>TONE CHECK</span>
              <span className="category-bubble" style={{ marginLeft: 10 }}>
                <img src="/img/logo.svg" alt="" className="w-4 h-4" />
                {parsed?.sentiment_tone?.overall_tone || "Neutral"}
              </span>
            </>
          }
          defaultOpen={false}
          ariaLabel="Toggle Tone Check"
        >
          {subjectTones.length > 0 && (
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 12,
              alignItems: "stretch",
            }}>
              {subjectTones.map((s, i) => (
                <div className="tile-metric" key={i}>
                  <div className="tile-title">re: {s.subject}</div>
                  <div className="spacer" />
                  <div className="category-bubble"><img src="/logo.svg" alt="" className="w-4 h-4" />{s.tone}</div>
                </div>
              ))}
            </div>
          )}
        </CollapsibleSection>

        {/* SUMMARY */}
        <CollapsibleSection headerLeft={<span> ARTICLE SUMMARY</span>} defaultOpen={false} ariaLabel="Toggle Summary">
          <div className="sentiment-desc">{parsed?.article_summary || "—"}</div>
        </CollapsibleSection>

        {/* OMISSION (each entry as sentiment-desc) */}
        <CollapsibleSection headerLeft={<span>POSSIBLE OMISSION</span>} defaultOpen={false} ariaLabel="Toggle Omission Analysis">
          {(() => {
            const omissions = parsed?.omission_analysis?.detected_omissions || [];
            if (!omissions.length) return <div className="sentiment-desc">No significant omission identified</div>;
            return (
              <div>
                {omissions.map((text, i) => (

                  <div key={i} className="sentiment-desc" style={{ marginBottom: 8 }}>
                    <img src="/img/missing.svg" alt="" className="w-4 h-4" />
                    {text}
                  </div>
                ))}
              </div>
            );
          })()}
        </CollapsibleSection>

        {/* ASSUMPTIONS ABOUT YOU */}
        <CollapsibleSection headerLeft={<span>ASSUMPTIONS ABOUT YOU</span>} defaultOpen={false} ariaLabel="Toggle Reader Assumptions">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 12,
              alignItems: "stretch",
            }}
          >
            {(parsed?.reader_assumptions || []).map((a, i) => (
              <div
                key={i}
                className="tile-metric"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  minHeight: "auto", // tweak if you want taller/shorter
                }}
              >
                {/* Centered bubble */}
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <span className="category-bubble">
                    <img src="/logo.svg" alt="" className="w-4 h-4" />
                    {a.type}
                  </span>
                </div>

                {/* Content pinned to bottom */}
                <div className="sentiment-desc" style={{ marginTop: 4, textAlign: "left" }}>
                  {a.assumption}
                </div>
              </div>
            ))}
          </div>

          <div className="hr" />
          <div className="sentiment-desc">{parsed?.influence_of_assumptions || ""}</div>
        </CollapsibleSection>


        {/* LANGUAGE CHECK */}
        <CollapsibleSection headerLeft={<span>LANGUAGE CHECK</span>} defaultOpen={false} ariaLabel="Toggle Language Check">
          {/* Top grid: category bubble (center) + quote (left) */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 12,
              alignItems: "stretch",
              gridAutoRows: "1fr", // keeps the row's tiles same height
            }}
          >
            {(parsed?.propaganda_techniques || []).map((p, i) => (
              <div
                key={i}
                className="tile-metric"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  minHeight: "auto",
                }}
              >
                {/* Centered bubble */}
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <span className="category-bubble">
                    <img src="/logo.svg" alt="" className="w-4 h-4" />
                    {p.technique}
                  </span>
                </div>

                {/* Quote below (left aligned, same text styling block you use elsewhere) */}
                <blockquote className="language-quote" style={{ marginTop: 10, textAlign: "center" }}>
                  <span className="quote-mark start">“&thinsp;</span>
                  {p.quote}
                  <span className="quote-mark end">&thinsp;”</span>
                </blockquote>

              </div>
            ))}
          </div>

          <div className="hr" />

          {/* Bottom grid: each explanation in its own tile (same sizing/centering) */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 12,
              alignItems: "stretch",
              gridAutoRows: "1fr",
            }}
          >
            {(parsed?.propaganda_techniques || []).map((p, i) => (
              <div
                key={i}
                className="sentiment-desc"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  minHeight: "auto",
                }}
              >
                {p.explanation}

              </div>
            ))}
          </div>
        </CollapsibleSection>

        {/* HEADLINE */}
        <CollapsibleSection
          headerLeft={
            <>
              <span>HEADLINE SPIN</span>
              <span className="category-bubble" style={{ marginLeft: 10 }}>
                <img src="/img/logo.svg" alt="" className="w-4 h-4" />
                {parsed?.title_analysis?.headline_category_selected || "—"}
              </span>
            </>
          }
          defaultOpen={false}
          ariaLabel="Toggle Headline Analysis"
        >
          <div className="sentiment-desc">
            {parsed?.title_analysis?.framing_detected || ""}
          </div>

          <div className="spacer" />
          <div className="sub alts-toolbar">
            <span className="sentiment-apend-logo">
              <img src="/img/shuffle.svg" alt="Shuffle" className="h-6 w-6" />
            </span>

            <span className="alts-label">Experiment with other Propaganda techniques:</span>

            <button
              className="bubble-btn alts-toggle"
              aria-expanded={altsOpen}
              aria-label={altsOpen ? "Collapse alternatives" : "Expand alternatives"}
              onClick={() => setAltsOpen((v) => !v)}
            >
              <img
                src={altsOpen ? "/img/expand-v.svg" : "/img/collapse-v.svg"}
                alt=""
                className="h-5 w-5"
              />
            </button>
          </div>

          {/* Smoothly collapsing group: tiles + active alt */}
          <Collapse open={altsOpen}>
            <div className="tiles3">
              {altTitles.map((t, i) => (
                <button
                  key={i}
                  onClick={() => setAltIdx(i)}
                  className="bubble-btn"
                  aria-pressed={altIdx === i}
                >
                  <span className="category-bubble">
                    <img src="/logo.svg" alt="" className="w-4 h-4" />
                    {t.category}
                  </span>
                </button>
              ))}
            </div>

            {activeAlt && (
              <div className="alt-list" aria-live="polite">
                <div className="labeled-box">
                  {activeAlt?.title || activeAlt?.example || ""}
                </div>
              </div>
            )}
          </Collapse>
        </CollapsibleSection>

        {/* PUBLICATION */}
        <CollapsibleSection
          headerLeft={<span>PUBLICATION: <span className="pub-name">{parsed?.source_publication?.name || "Unknown publisher"}</span></span>}
          defaultOpen={false}
          ariaLabel="Toggle Publication Analysis"
        >
          <div className="sentiment-desc">{parsed?.source_publication?.overview || "unknown"}</div>
        </CollapsibleSection>
      </div>
    </>
  );
}
